import React, { useEffect, useState } from "react";
import { graphql } from "gatsby";
import { NewsListing } from "./NewsListing";
import {
  KontentArticleOrSotDPage,
  KontentComponentSettings,
  KontentContentHedgehog,
  KontentContentProgram,
  KontentLinkedArticleOrSotDPage,
  KontentLinkedContentProgram,
} from "../../../types";
import { getMultiChoice } from "../../../lib/multichoice";
import { ContentTileProps } from "../../elements/ContentTile/ContentTile";
import {
  formatProgram,
  formatHedgehog,
  formatArticleSotd,
} from "../../../lib/news";
import { useRecentContent } from "../../../lib/useRecentContent";
import { useArticles, Article } from "../../../lib/useArticles";
import { useScientists } from "../../../lib/useScientists";

type NewsListingTile =
  | KontentArticleOrSotDPage
  | KontentContentHedgehog
  | KontentContentProgram;

interface Props extends KontentComponentSettings {
  featured_green_tiles: {
    value: (NewsListingTile & { __typename: string })[];
  };
  featured_program: KontentLinkedContentProgram;
  featured_news_item: KontentLinkedArticleOrSotDPage;
}

const NewsListingContainer: React.FC<Props> = ({
  id,
  component_settings__anchor_name,
  component_settings__hide_on_mobile,
  featured_program,
  featured_news_item,
  featured_green_tiles,
}) => {
  const ARTICLES_PAGINATED_COLLECTION_ROUTE = "/paginated-collections/";
  const recentContent = useRecentContent();
  const recentScientist = useScientists()
    .scientists.slice(0, 1)
    .map((scientist) => ({
      subhead: scientist.page?.title,
      ctaHref: scientist.page?.url,
      contentCategory: scientist.contentTile?.contentCategory,
      text: scientist.contentTile?.text,
      date:
        scientist.page?.displayPublishDate &&
        new Date(scientist.page.displayPublishDate).toLocaleDateString(
          "en-us",
          {
            month: "long",
            day: "numeric",
            year: "numeric",
          }
        ),
      image: {
        largeImage: scientist.contentTile?.image,
      },
    }))[0];
  const articleQuery = useArticles();
  const pages = articleQuery.pages;

  const checkForDuplicateArticle = (article: Article) => {
    const isNotFeatured =
      !article.page?.url ||
      !featured_news_item.value[0]?.elements.url.url.includes(
        article.page?.url
      );
    const isNotGreenTile = featured_green_tiles.value.filter((tile) => {
      return (
        tile["__typename"] !== "kontent_item_page" ||
        !tile.elements.url?.url.includes(article.page?.url)
      );
    });

    return isNotFeatured && isNotGreenTile;
  };

  const articles = articleQuery.articles.filter(checkForDuplicateArticle);

  const [offset, setOffset] = useState(
    articleQuery.articles.length - articles.length
  );

  const [visibleArticles, setVisibleArticles] = useState(articles);

  const [nextPage, setNextPage] = useState(pages[0]);
  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(nextPage.hasNextPage);

  useEffect(() => {
    const fetchArticles = async () => {
      await fetch(ARTICLES_PAGINATED_COLLECTION_ROUTE + nextPage.id + ".json")
        .then((response) => response.json())
        .then((data) => {
          const dedupedData = data.nodes.filter(checkForDuplicateArticle);

          setVisibleArticles([...visibleArticles, ...dedupedData]);

          setOffset(offset + data.nodes.length - dedupedData.length);
        });
    };

    if (loadMore && hasMore) {
      fetchArticles();

      if (nextPage.hasNextPage) {
        setNextPage(pages[nextPage.index + 1]);
      } else {
        setHasMore(false);
      }

      setLoadMore(false);
    }
  }, [loadMore]);

  const formattedGreenTiles = featured_green_tiles.value.map((tile) => {
    let contentTile = {} as ContentTileProps | undefined;

    switch (tile["__typename"]) {
      case "kontent_item_page":
        contentTile = formatArticleSotd(tile as KontentArticleOrSotDPage);
        break;
      case "kontent_item_content___hedgehog":
        contentTile = formatHedgehog(tile as KontentContentHedgehog);
        break;
      default:
        contentTile = formatProgram(tile as KontentContentProgram);
    }

    return contentTile;
  });

  const featuredProgram = featured_program.value[0]
    ? formatProgram(featured_program.value[0])
    : recentContent.programs[0];
  const featuredNewsItem = featured_news_item?.value[0]
    ? formatArticleSotd(featured_news_item.value[0])
    : articleQuery.articles
        .slice(0, 1)
        .map((article) => article.contentTile)[0];
  const featuredGreenTiles =
    featured_green_tiles?.value?.length > 0
      ? [...formattedGreenTiles, recentScientist, recentContent.hedgehog].slice(
          0,
          2
        )
      : [recentScientist, recentContent.hedgehog];

  return (
    <NewsListing
      id={id}
      anchor={component_settings__anchor_name.value}
      hideOnMobile={
        getMultiChoice(component_settings__hide_on_mobile) === "yes"
      }
      featuredProgram={featuredProgram}
      featuredNewsItem={featuredNewsItem}
      featuredGreenTiles={
        featuredGreenTiles.filter(
          (tile) => tile !== undefined
        ) as ContentTileProps[]
      }
      articles={visibleArticles}
      hasMore={hasMore}
      setLoadMore={setLoadMore}
      programs={recentContent.programs}
    />
  );
};

export default NewsListingContainer;

export const fragmentNewsListing = graphql`
  fragment NewsListing on kontent_item_component___news_listing {
    elements {
      component_settings__anchor_name {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
      featured_green_tiles {
        value {
          __typename
        }
        ...kontentContentHedgehog
        ...kontentContentProgram
        ...kontentArticleOrSotDPage
      }
      featured_news_item {
        ...kontentArticleOrSotDPage
      }
      featured_program {
        ...kontentContentProgram
      }
    }
  }
`;
